import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, Save, XCircle, FileText, Loader2, ExternalLink, Image as ImageIcon, Bold, Italic, Heading } from 'lucide-react';
import api from '../../utils/axios';
import getFullUrl from '../../utils/formatUrl';

const BlogManager = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const editorRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    author: 'Admin',
    date: new Date().toLocaleDateString('en-GB'),
    excerpt: '',
    content: '',
    image: '',
    category: 'Campus News',
    active: true,
  });

  useEffect(() => {
    fetchBlogs();
  }, []);

  // When editing starts, set the editor content
  useEffect(() => {
    if (showForm && editorRef.current) {
      // Only update innerHTML if it doesn't match to avoid interrupting user input
      if (editorRef.current.innerHTML !== formData.content && document.activeElement !== editorRef.current) {
        editorRef.current.innerHTML = formData.content || '';
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm, editingId]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const res = await api.get('/blogs');
      setBlogs(res.data.data);
    } catch (err) {
      console.error('Failed to fetch blogs', err);
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)+/g, '');
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData(prev => ({
      ...prev,
      title,
      slug: prev.slug === generateSlug(prev.title) || !prev.slug ? generateSlug(title) : prev.slug
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    setUploading(true);
    try {
      const res = await api.post('/upload', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, image: res.data.url }));
    } catch (err) {
      alert("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    // Final sync from editor to formData
    const finalContent = editorRef.current ? editorRef.current.innerHTML : formData.content;
    
    try {
      const payload = { ...formData, content: finalContent };
      if (editingId) {
        await api.put(`/blogs/${editingId}`, payload);
      } else {
        await api.post('/blogs', payload);
      }

      resetFormData();
      setEditingId(null);
      setShowForm(false);
      fetchBlogs();
    } catch (err) {
      alert('Failed to save blog: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const resetFormData = () => {
    setFormData({
      title: '',
      slug: '',
      author: 'Admin',
      date: new Date().toLocaleDateString('en-GB'),
      excerpt: '',
      content: '',
      image: '',
      category: 'Campus News',
      active: true,
    });
    if (editorRef.current) {
        try {
            editorRef.current.innerHTML = '';
        } catch (e) {
            console.warn("Editor ref was not ready for clearing", e);
        }
    }
  };

  const handleEdit = (blog) => {
    setFormData({
      title: blog.title,
      slug: blog.slug,
      author: blog.author,
      date: blog.date,
      excerpt: blog.excerpt,
      content: blog.content,
      image: blog.image,
      category: blog.category,
      active: blog.active,
    });
    setEditingId(blog._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this blog post?')) {
      try {
        await api.delete(`/blogs/${id}`);
        setBlogs(blogs.filter(b => b._id !== id));
      } catch (err) {
        alert('Failed to delete');
      }
    }
  };

  const cancelForm = () => {
    resetFormData();
    setEditingId(null);
    setShowForm(false);
  };

  const execCmd = (cmd, val = null) => {
    document.execCommand(cmd, false, val);
    if (editorRef.current) {
      setFormData(prev => ({ ...prev, content: editorRef.current.innerHTML }));
    }
  };

  return (
    <div className="space-y-8">
      <div className="bg-gradient-to-r from-[#212529] to-[#343a40] text-white p-8 rounded-sm relative overflow-hidden shadow-2xl">
        <div className="relative z-10 flex justify-between items-center">
          <div>
            <h4 className="text-2xl font-black uppercase tracking-tighter mb-2 italic flex items-center">
              <FileText size={28} className="mr-3 text-[#ee3124]" /> Blog & Stories Management
            </h4>
            <p className="text-[10px] font-bold uppercase tracking-widest text-white/70 max-w-xl">
              Create and manage dynamic blog posts. Posts will be accessible via their unique URL slugs.
            </p>
          </div>
          <button
            onClick={() => { cancelForm(); setShowForm(true); }}
            className="bg-[#ee3124] text-white px-6 py-3 rounded-sm font-black uppercase text-xs tracking-widest flex items-center hover:bg-white hover:text-[#ee3124] transition-all shadow-xl"
          >
            <Plus className="w-4 h-4 mr-2" /> Write New Story
          </button>
        </div>
      </div>

      {showForm && (
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-8 animate-scaleIn">
          <div className="flex justify-between items-center mb-8">
            <h3 className="text-lg font-black uppercase tracking-tighter text-slate-900 border-l-4 border-[#ee3124] pl-4">
              {editingId ? 'Edit Article' : 'New Article'}
            </h3>
            <button onClick={cancelForm} className="text-slate-400 hover:text-red-500">
              <XCircle size={22} />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Article Title</label>
                <input
                  type="text"
                  required
                  className="w-full border-b-2 border-slate-100 py-3 outline-none focus:border-[#ee3124] font-bold text-sm"
                  value={formData.title}
                  onChange={handleTitleChange}
                  placeholder="e.g. Unlocking Growth with Strategic Brand Services"
                />
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">URL Slug (Dynamic name)</label>
                <div className="relative">
                  <input
                    type="text"
                    required
                    className="w-full border-b-2 border-slate-100 py-3 outline-none focus:border-[#ee3124] font-bold text-sm text-slate-400"
                    value={formData.slug}
                    onChange={e => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                    placeholder="unlocking-growth-strategic-brand"
                  />
                  <div className="absolute right-0 top-3 text-[9px] font-black uppercase text-slate-300">/blog-details?name={formData.slug}</div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Author</label>
                <input
                  type="text"
                  className="w-full border-b-2 border-slate-100 py-3 outline-none focus:border-[#ee3124] font-bold text-sm"
                  value={formData.author}
                  onChange={e => setFormData({ ...formData, author: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Category</label>
                <select
                  className="w-full border-b-2 border-slate-100 py-3 outline-none focus:border-[#ee3124] font-bold text-sm bg-transparent"
                  value={formData.category}
                  onChange={e => setFormData({ ...formData, category: e.target.value })}
                >
                  <option value="Campus News">Campus News</option>
                  <option value="Academic">Academic</option>
                  <option value="Student Life">Student Life</option>
                  <option value="Events">Events</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Display Date</label>
                <input
                  type="text"
                  className="w-full border-b-2 border-slate-100 py-3 outline-none focus:border-[#ee3124] font-bold text-sm"
                  value={formData.date}
                  onChange={e => setFormData({ ...formData, date: e.target.value })}
                  placeholder="03-04-2026"
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Hero Image</label>
              <div className="flex items-center space-x-4">
                <label className={`flex-1 cursor-pointer px-4 py-3 bg-slate-50 border-2 border-dashed border-slate-200 rounded-sm text-xs font-bold text-slate-500 hover:border-[#ee3124] transition-all flex items-center justify-center ${uploading ? 'opacity-50' : ''}`}>
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ImageIcon className="w-4 h-4 mr-2" />}
                  {uploading ? 'Uploading Image...' : formData.image ? 'Image Uploaded' : 'Select Featured Image'}
                  <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} disabled={uploading} />
                </label>
                {formData.image && (
                  <div className="w-16 h-12 rounded-sm overflow-hidden border border-slate-200">
                    <img src={getFullUrl(formData.image)} className="w-full h-full object-cover" alt="Preview" />
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Short Excerpt (Teaser text)</label>
              <textarea
                required
                rows={2}
                className="w-full border-2 border-slate-100 p-4 outline-none focus:border-[#ee3124] font-medium text-sm rounded-sm"
                value={formData.excerpt}
                onChange={e => setFormData({ ...formData, excerpt: e.target.value })}
                placeholder="Brief summary of the article for the list page..."
              />
            </div>

            <div>
              <div className="flex justify-between items-end mb-2">
                <label className="block text-[10px] font-black uppercase text-slate-500">Full Article Story (Visual Rich Editor)</label>
                <div className="flex bg-slate-100 rounded-sm overflow-hidden border border-slate-200 shadow-inner">
                   <button type="button" onClick={() => execCmd('bold')} className="p-2 hover:bg-white text-slate-600 transition-colors border-r border-slate-200" title="Bold"><Bold size={14} /></button>
                   <button type="button" onClick={() => execCmd('italic')} className="p-2 hover:bg-white text-slate-600 transition-colors border-r border-slate-200" title="Italic"><Italic size={14} /></button>
                   <button type="button" onClick={() => execCmd('formatBlock', 'h3')} className="p-2 hover:bg-white text-slate-600 transition-colors border-r border-slate-200" title="Heading"><Heading size={14} /></button>
                   <button type="button" onClick={() => execCmd('formatBlock', 'p')} className="p-2 hover:bg-white text-slate-600 transition-colors text-[10px] font-black px-3" title="Paragraph">P</button>
                </div>
              </div>
              
              <div
                ref={editorRef}
                contentEditable
                dir="ltr"
                style={{ direction: 'ltr', textAlign: 'left' }}
                className="w-full min-h-[400px] border-2 border-slate-100 p-6 outline-none focus:border-[#ee3124] font-medium text-sm rounded-sm bg-slate-50/30 overflow-y-auto prose prose-sm max-w-none prose-slate text-left"
                onInput={(e) => {
                  const html = e.currentTarget.innerHTML;
                  setFormData(prev => ({ ...prev, content: html }));
                }}
              />
              
              <p className="text-[9px] text-slate-400 mt-2 font-bold uppercase tracking-widest">Formatting from Word, Docs, or websites will be preserved automatically on paste.</p>
            </div>

            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="active"
                checked={formData.active}
                onChange={e => setFormData({ ...formData, active: e.target.checked })}
                className="w-4 h-4 accent-[#ee3124]"
              />
              <label htmlFor="active" className="text-sm font-bold text-slate-700">Publish Article (Live on site)</label>
            </div>

            <div className="flex gap-4 pt-4 border-t border-slate-100">
              <button
                type="submit"
                disabled={saving || uploading}
                className="bg-[#212529] text-white px-10 py-4 font-black uppercase text-xs tracking-[3px] shadow-xl hover:bg-[#ee3124] transition-all disabled:opacity-50 flex items-center"
              >
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving Story...</> : <><Save className="w-4 h-4 mr-2" /> {editingId ? 'Update' : 'Publish'} Story</>}
              </button>
              <button
                type="button"
                onClick={cancelForm}
                className="bg-slate-100 text-slate-600 px-8 py-4 font-black uppercase text-xs tracking-widest hover:bg-slate-200 transition-all"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-sm shadow-sm">
        <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
          <h3 className="text-sm font-black uppercase tracking-widest text-slate-600">
            Published Stories ({blogs.length})
          </h3>
        </div>

        {loading ? (
          <div className="p-20 text-center">
            <Loader2 size={32} className="animate-spin text-slate-300 mx-auto" />
            <p className="text-slate-400 font-bold text-xs mt-4 uppercase tracking-widest">Reading archive...</p>
          </div>
        ) : blogs.length === 0 ? (
          <div className="p-20 text-center">
            <FileText size={48} className="text-slate-200 mx-auto mb-4" />
            <p className="text-slate-400 font-bold text-sm">No articles written yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 text-left border-b border-slate-100">
                  <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Story Detail</th>
                  <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Category</th>
                  <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Slug / Name</th>
                  <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Status</th>
                  <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {blogs.map((blog) => (
                  <tr key={blog._id} className="hover:bg-slate-50/80 transition-colors group">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-10 bg-[#ee3124] rounded-sm overflow-hidden flex-shrink-0">
                          {blog.image ? (
                            <img src={getFullUrl(blog.image)} className="w-full h-full object-cover" alt="" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-white font-bold text-[8px]">BLOG</div>
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-black text-[#212529] uppercase tracking-tight line-clamp-1">{blog.title}</p>
                          <p className="text-[10px] font-bold text-slate-400 uppercase">{blog.date} | By {blog.author}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 bg-slate-100 text-[#212529] rounded-sm text-[10px] font-black uppercase tracking-tighter">
                        {blog.category}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-[10px] font-bold text-[#ee3124] uppercase tracking-widest underline decoration-dotted">
                        ?name={blog.slug}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase ${blog.active ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-500'}`}>
                        {blog.active ? 'Live' : 'Draft'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                         <a 
                          href={`/blog-details?name=${blog.slug}`}
                          target="_blank"
                          rel="noreferrer"
                          className="p-2 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded-sm transition-all"
                        >
                          <ExternalLink size={16} />
                        </a>
                        <button
                          onClick={() => handleEdit(blog)}
                          className="p-2 text-slate-400 hover:text-amber-500 hover:bg-amber-50 rounded-sm transition-all"
                        >
                          <Save size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(blog._id)}
                          className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-sm transition-all"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogManager;
